'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Trophy, Plus } from '@phosphor-icons/react';
import { Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ai-elements/skeleton';
import { useDocumentTitle } from '@/lib/use-document-title';
import type {
  ArenaExperiment,
  ArenaModality,
  ArenaExperimentStatus,
  ArenaGenerationStatus,
} from '@/lib/arena/types';
import { ModalityBadge, StatusChip, SpendMeter } from '@/components/arena/badges';

interface ListItem {
  experiment: ArenaExperiment;
  generationCounts: Partial<Record<ArenaGenerationStatus, number>>;
}

export function ArenaListClient() {
  useDocumentTitle('Arena');
  const router = useRouter();
  const [items, setItems] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch('/api/arena/experiments');
      if (!res.ok) throw new Error(`Failed to load experiments (${res.status})`);
      const data = await res.json();
      setItems(Array.isArray(data.experiments) ? data.experiments : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load experiments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      <Toaster position="top-center" richColors closeButton />
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <div className="flex items-center gap-2">
            <Trophy weight="duotone" className="size-5 text-amber-400" />
            <div>
              <h1 className="text-sm font-extrabold leading-none tracking-tight">Arena</h1>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Blind eval + model bake-offs across every modality.
              </p>
            </div>
          </div>
          <div className="ml-auto">
            <Button
              size="sm"
              className="h-8 rounded-full bg-violet-500 px-4 text-xs font-extrabold text-white hover:bg-violet-400"
              onClick={() => router.push('/arena/new')}
            >
              <Plus className="mr-1 size-3.5" weight="bold" />
              New experiment
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 md:px-6">
          {error && (
            <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-40 rounded-2xl" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex min-h-[440px] flex-col items-center justify-center text-center">
              <div className="grid size-14 place-items-center rounded-2xl bg-amber-500/12">
                <Trophy weight="duotone" className="size-7 text-amber-400" />
              </div>
              <p className="mt-6 text-base font-semibold">Run your first Arena experiment</p>
              <p className="mx-auto mt-1.5 max-w-lg text-sm text-muted-foreground">
                An Arena experiment defines a task and a matrix of variants — different
                models, prompts, or configs — then generates candidates across image,
                video, avatar, or HTML-viz modalities. You score them side-by-side in a
                blind, keyboard-fast review flow; the Arena records full provenance
                (cost, latency, seed, prompt) and ranks the variants so measured quality
                replaces vibes.
              </p>
              <Button
                size="sm"
                className="mt-6 rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
                onClick={() => router.push('/arena/new')}
              >
                <Plus className="mr-1.5 size-4" weight="bold" />
                New experiment
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
              {items.map(({ experiment, generationCounts }) => (
                <ExperimentCard key={experiment.id} experiment={experiment} counts={generationCounts} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function ExperimentCard({
  experiment,
  counts,
}: {
  experiment: ArenaExperiment;
  counts: Partial<Record<ArenaGenerationStatus, number>>;
}) {
  const total = (counts.queued ?? 0) + (counts.running ?? 0) + (counts.succeeded ?? 0) + (counts.failed ?? 0);
  return (
    <Link
      href={`/arena/${experiment.id}`}
      className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-card/40 p-[18px] transition-colors hover:border-violet-500/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{experiment.name}</p>
          {experiment.description && (
            <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{experiment.description}</p>
          )}
        </div>
        <StatusChip status={experiment.status as ArenaExperimentStatus} />
      </div>

      <div className="flex items-center gap-2">
        <ModalityBadge modality={experiment.modality as ArenaModality} />
        <span className="text-[10px] text-muted-foreground">
          {new Date(experiment.createdAt).toLocaleDateString()}
        </span>
      </div>

      <SpendMeter spent={experiment.costSpent} cap={experiment.budgetCapCredits} />

      <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
        <span>{total} gens</span>
        {(counts.succeeded ?? 0) > 0 && <span className="text-emerald-400">{counts.succeeded} ok</span>}
        {(counts.running ?? 0) > 0 && <span className="text-sky-400">{counts.running} running</span>}
        {(counts.queued ?? 0) > 0 && <span>{counts.queued} queued</span>}
        {(counts.failed ?? 0) > 0 && <span className="text-red-400">{counts.failed} failed</span>}
      </div>
    </Link>
  );
}
